/* eslint-disable no-restricted-globals */
import {
  ElementInteractionsOptions,
  ActionType,
  getDecodeURI,
  IDiagnosticsClient,
  MASKED_TEXT_VALUE,
  TEXT_MASK_ATTRIBUTE,
  getPageTitle,
  replaceSensitiveString,
} from '@amplitude/analytics-core';
import type { DataSource } from '@amplitude/analytics-core/lib/esm/types/element-interactions';
import * as constants from './constants';
import {
  removeEmptyProperties,
  extractPrefixedAttributes,
  isElementPointerCursor,
  getClosestElement,
  isElementBasedEvent,
  parseAttributesToMask,
} from './helpers';
import type { BaseTimestampedEvent, ElementBasedTimestampedEvent, TimestampedEvent, JSONValue } from './helpers';
import { getAncestors, getElementProperties } from './hierarchy';
import { getDataSource } from './pageActions/actions';
import { Hierarchy } from './typings/autocapture';

export class DataExtractor {
  private readonly additionalMaskTextPatterns: RegExp[];
  diagnosticsClient?: IDiagnosticsClient;

  constructor(options: ElementInteractionsOptions, context?: { diagnosticsClient: IDiagnosticsClient }) {
    this.diagnosticsClient = context?.diagnosticsClient;

    const rawPatterns = options.maskTextRegex ?? [];

    const compiled: RegExp[] = [];
    for (const entry of rawPatterns) {
      if (compiled.length >= constants.MAX_MASK_TEXT_PATTERNS) {
        break;
      }
      if (entry instanceof RegExp) {
        compiled.push(entry);
      } else if ('pattern' in entry && typeof entry.pattern === 'string') {
        try {
          compiled.push(new RegExp(entry.pattern, 'i'));
        } catch {
          // ignore invalid pattern strings
        }
      }
    }
    this.additionalMaskTextPatterns = compiled;
  }

  /**
   * Wrapper method to replace sensitive strings using the helper function
   * @param text - The text to search for sensitive data
   * @returns The text with sensitive data replaced by masked text
   */
  replaceSensitiveString = (text: string | null): string => {
    return replaceSensitiveString(text, this.additionalMaskTextPatterns);
  };

  // Get the DOM hierarchy of the element, starting from the target element to the root element.
  getHierarchy = (element: Element | null): Hierarchy => {
    const startTime = performance.now();

    let hierarchy: Hierarchy = [];
    if (!element) {
      return [];
    }

    // Get list of ancestors including itself and get properties at each level in the hierarchy
    const ancestors = getAncestors(element);

    // Build attributes to mask map
    const elementToAttributesToMaskMap = new Map<Element, Set<string>>();

    for (let i = ancestors.length - 1; i >= 0; i--) {
      const node = ancestors[i];
      if (node) {
        const attributesToMask = parseAttributesToMask(node.getAttribute(constants.DATA_AMP_MASK_ATTRIBUTES));
        const ancestorAttributesToMask =
          i === ancestors.length - 1 ? [] : elementToAttributesToMaskMap.get(ancestors[i + 1]) ?? new Set<string>();
        const combinedAttributesToMask = new Set([...ancestorAttributesToMask, ...attributesToMask]);
        elementToAttributesToMaskMap.set(node, combinedAttributesToMask);
      }
    }

    hierarchy = ancestors.map((el) =>
      getElementProperties(el, elementToAttributesToMaskMap.get(el) ?? new Set<string>()),
    );

    // Search for and mask any sensitive attribute values
    for (const hierarchyNode of hierarchy) {
      if (hierarchyNode?.attrs) {
        Object.entries(hierarchyNode.attrs).forEach(([key, value]) => {
          if (hierarchyNode.attrs) {
            hierarchyNode.attrs[key] = this.replaceSensitiveString(value);
          }
        });
      }
    }

    const endTime = performance.now();
    this.diagnosticsClient?.recordHistogram('autocapturePlugin.getHierarchy', endTime - startTime);

    return hierarchy;
  };

  getNearestLabel = (element: Element): string => {
    const parent = element.parentElement;
    if (!parent) {
      return '';
    }
    let labelElement: Element | null;
    try {
      labelElement = parent.querySelector(':scope>span,h1,h2,h3,h4,h5,h6');
    } catch {
      /* istanbul ignore next */
      labelElement = null;
    }
    if (labelElement) {
      /* istanbul ignore next */
      return this.getText(labelElement);
    }
    return this.getNearestLabel(parent);
  };

  // Returns the Amplitude event properties for the given element.
  getEventProperties = (actionType: ActionType, element: Element, dataAttributePrefix: string) => {
    /* istanbul ignore next */
    const tag = element?.tagName?.toLowerCase?.();
    /* istanbul ignore next */
    const rect =
      typeof element.getBoundingClientRect === 'function' ? element.getBoundingClientRect() : { left: null, top: null };

    const hierarchy = this.getHierarchy(element);
    const currentElementAttributes = hierarchy[0]?.attrs;
    const nearestLabel = this.getNearestLabel(element);
    const attributes = extractPrefixedAttributes(currentElementAttributes ?? {}, dataAttributePrefix);

    /* istanbul ignore next */
    const properties: Record<string, any> = {
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_HIERARCHY]: hierarchy,
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_TAG]: tag,
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_TEXT]: this.getText(element),
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_POSITION_LEFT]: rect.left == null ? null : Math.round(rect.left),
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_POSITION_TOP]: rect.top == null ? null : Math.round(rect.top),
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_ATTRIBUTES]: attributes,
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_PARENT_LABEL]: nearestLabel,
      [constants.AMPLITUDE_EVENT_PROP_PAGE_URL]: getDecodeURI(window.location.href.split('?')[0]),
      [constants.AMPLITUDE_EVENT_PROP_PAGE_TITLE]: (
        getPageTitle as (parseTitleFunction: (title: string) => string) => string
      )(this.replaceSensitiveString),
      [constants.AMPLITUDE_EVENT_PROP_VIEWPORT_HEIGHT]: window.innerHeight,
      [constants.AMPLITUDE_EVENT_PROP_VIEWPORT_WIDTH]: window.innerWidth,
    };

    // id is never masked, so always include it
    properties[constants.AMPLITUDE_EVENT_PROP_ELEMENT_ID] = element.getAttribute('id') || '';

    // class is never masked, so always include it
    properties[constants.AMPLITUDE_EVENT_PROP_ELEMENT_CLASS] = element.getAttribute('class');

    properties[constants.AMPLITUDE_EVENT_PROP_ELEMENT_ARIA_LABEL] = currentElementAttributes?.['aria-label'];

    if (tag === 'a' && actionType === 'click' && element instanceof HTMLAnchorElement) {
      properties[constants.AMPLITUDE_EVENT_PROP_ELEMENT_HREF] = this.replaceSensitiveString(element.href); // we don't use hierarchy here because we don't want href value to be changed
    }

    return removeEmptyProperties(properties);
  };

  addAdditionalEventProperties = <T>(
    event: T,
    type: TimestampedEvent<T>['type'],
    selectorAllowlist: string[],
    dataAttributePrefix: string,
    // capture the event if the cursor is a "pointer" when this element is clicked on
    // reason: a "pointer" cursor indicates that an element should be interactable
    //         regardless of the element's tag name
    isCapturingCursorPointer = false,
  ): TimestampedEvent<T> | ElementBasedTimestampedEvent<T> => {
    const baseEvent: BaseTimestampedEvent<T> | ElementBasedTimestampedEvent<T> = {
      event,
      timestamp: Date.now(),
      type,
    };

    if (isElementBasedEvent(baseEvent) && baseEvent.event.target !== null) {
      if (isCapturingCursorPointer) {
        const isCursorPointer = isElementPointerCursor(baseEvent.event.target as Element, baseEvent.type);
        if (isCursorPointer) {
          baseEvent.closestTrackedAncestor = baseEvent.event.target as HTMLElement;
          baseEvent.targetElementProperties = this.getEventProperties(
            baseEvent.type,
            baseEvent.closestTrackedAncestor,
            dataAttributePrefix,
          );
          return baseEvent;
        }
      }
      // Retrieve additional event properties from the target element
      const closestTrackedAncestor = getClosestElement(baseEvent.event.target as HTMLElement, selectorAllowlist);
      if (closestTrackedAncestor) {
        baseEvent.closestTrackedAncestor = closestTrackedAncestor;
        baseEvent.targetElementProperties = this.getEventProperties(
          baseEvent.type,
          closestTrackedAncestor,
          dataAttributePrefix,
        );
      }
      return baseEvent;
    }

    return baseEvent;
  };

  extractDataFromDataSource = (dataSource: DataSource, contextElement: HTMLElement) => {
    // Extract from DOM Element
    if (dataSource.sourceType === 'DOM_ELEMENT') {
      const sourceElement = getDataSource(dataSource, contextElement);
      if (!sourceElement) {
        return undefined;
      }

      if (dataSource.elementExtractType === 'TEXT') {
        return this.getText(sourceElement);
      } else if (dataSource.elementExtractType === 'ATTRIBUTE' && dataSource.attribute) {
        return sourceElement.getAttribute(dataSource.attribute);
      }
      return undefined;
    }

    // TODO: Extract from other source types
    return undefined;
  };

  getText = (element: Element): string => {
    // Check if element or any parent has data-amp-mask attribute
    const hasMaskAttribute = element.closest(`[${TEXT_MASK_ATTRIBUTE}]`) !== null;
    if (hasMaskAttribute) {
      return MASKED_TEXT_VALUE;
    }
    let output = '';
    if (!element.querySelector(`[${TEXT_MASK_ATTRIBUTE}], [contenteditable]`)) {
      output = (element as HTMLElement).innerText || '';
    } else {
      const clonedTree = element.cloneNode(true) as HTMLElement;
      // replace all elements with TEXT_MASK_ATTRIBUTE attribute and contenteditable with the text MASKED_TEXT_VALUE
      clonedTree.querySelectorAll(`[${TEXT_MASK_ATTRIBUTE}], [contenteditable]`).forEach((node) => {
        (node as HTMLElement).innerText = MASKED_TEXT_VALUE;
      });
      output = clonedTree.innerText || '';
    }
    return this.replaceSensitiveString(output.substring(0, 255)).replace(/\s+/g, ' ').trim();
  };

  // Returns the element properties for the given element in Visual Labeling.
  getEventTagProps = (element: Element): Record<string, JSONValue> => {
    if (!element) {
      return {};
    }
    /* istanbul ignore next */
    const tag = element?.tagName?.toLowerCase?.();

    const properties = {
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_TAG]: tag,
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_TEXT]: this.getText(element),
      [constants.AMPLITUDE_EVENT_PROP_PAGE_URL]: window.location.href.split('?')[0],
    };
    return removeEmptyProperties(properties) as Record<string, JSONValue>;
  };
}
